'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { c } from '../../../utils/tailwind-merge';
import { IconButton } from '../../atoms/Button/IconButton';
import { InputV2 } from '../../atoms/Input.v2';
import { AccountTypeToggleGroup } from '../../molecules/AccountTypeToggleGroup/AccountTypeToggleGroup';
import { Banner } from '../../molecules/Banner';
import { FormModal } from '../../molecules/FormModal/FormModal';
import { VisibilityToggleGroup } from '../../molecules/VisibilityToggleGroup/VisibilityToggleGroup';
import {
  LIST_NAME_MAX_LENGTH,
  favoriteListModalFormSchema,
} from './FavoriteListModal.schema';
import type {
  FavoriteListModalFormData,
  FavoriteListModalProps,
} from './FavoriteListModal.types';

type FormLabelProps = {
  htmlFor: string;
  children: React.ReactNode;
  className?: string;
};

const FormLabel = ({ htmlFor, children, className = '' }: FormLabelProps) => (
  <label
    htmlFor={htmlFor}
    className={c(
      'text-sm font-normal leading-6 tracking-tight text-grey-100',
      className
    )}
  >
    {children}
  </label>
);

export const FavoriteListModal = ({
  listName,
  visibility,
  accountType,
  showAccountType,
  userName,
  userProfilePicture,
  publisherName,
  microcopy,
  onSave,
  isSubmitting = false,
  trigger,
  open,
  onOpenChange,
  onDismissError,
  position,
  showBackdrop,
  serverError,
  limitReached = false,
  className = '',
}: FavoriteListModalProps) => {
  const defaultFormValues = useMemo(
    () => ({
      listName,
      visibility,
      accountType: showAccountType ? accountType : undefined,
    }),
    [listName, visibility, accountType, showAccountType]
  );

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    watch,
    reset,
  } = useForm<FavoriteListModalFormData>({
    resolver: zodResolver(favoriteListModalFormSchema),
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  const watchedListName = watch('listName');

  const prevOpenRef = useRef(open);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      reset(defaultFormValues);
    }
    prevOpenRef.current = open;
  }, [open, reset, defaultFormValues]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      reset(defaultFormValues);
      onDismissError?.();
    }
    onOpenChange?.(isOpen);
  };

  const onFormSubmit = async (data: FavoriteListModalFormData) => {
    if (limitReached) return;
    try {
      await onSave({
        listName: data.listName,
        visibility: data.visibility,
        ...(showAccountType &&
          data.accountType && { accountType: data.accountType }),
      });
    } catch (error) {
      console.log('error', error);
      throw error;
    }
  };

  const handleOnCancelFavorite = () => {
    reset(defaultFormValues);
  };

  const isControlled = open !== undefined;
  const triggerElement =
    trigger ??
    (isControlled ? undefined : (
      <IconButton
        className="h-12 w-12 overflow-hidden rounded-lg bg-grey-700 hover:bg-grey-800"
        iconName="BookmarkSimple"
        ariaLabel={microcopy.cta}
        iconWeight="bold"
        iconSize={24}
        iconClassName="fill-white hover:fill-white"
      />
    ));

  const formContent = (
    <div className="flex flex-col py-4">
      {limitReached && microcopy.limitMessage && (
        <div className="mb-5" data-test="limit-message-alert">
          <Banner
            type="infoBlackBg"
            title={microcopy.limitMessage.title}
            description={microcopy.limitMessage.description}
            dismiss={microcopy.limitMessage.dismiss}
            onDismiss={onDismissError ?? (() => {})}
            style="fill"
          />
        </div>
      )}
      {serverError && !limitReached && microcopy.serverMessage && (
        <div className="mb-5" data-test="server-message-alert">
          <Banner
            type="errorBlackBg"
            title={microcopy.serverMessage.title}
            description={microcopy.serverMessage.description}
            dismiss={microcopy.serverMessage.dismiss}
            onDismiss={onDismissError ?? (() => {})}
            style="fill"
          />
        </div>
      )}
      <form
        id="favorite-list-form"
        onSubmit={handleSubmit(onFormSubmit)}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col gap-2">
          <FormLabel htmlFor="list-name-input">
            {microcopy.listNameLabel}
          </FormLabel>
          <div className="relative">
            <Controller
              control={control}
              name="listName"
              render={({ field }) => (
                <InputV2
                  id="list-name-input"
                  dark
                  autoComplete="off"
                  {...field}
                  placeholder={microcopy.listNamePlaceholder}
                  className="w-full pr-10"
                  maxLength={LIST_NAME_MAX_LENGTH}
                  showRemainingChars
                />
              )}
            />
            {watchedListName && (
              <IconButton
                iconName="XCircle"
                ariaLabel={microcopy.clearInput}
                className="absolute right-2.5 top-[10px] h-5 w-5 bg-transparent"
                iconClassName="fill-grey-500 hover:fill-grey-500"
                iconWeight="fill"
                onClick={() =>
                  setValue('listName', '', { shouldValidate: true })
                }
                iconSize={20}
              />
            )}
          </div>
        </div>

        {showAccountType && (
          <div className="flex flex-col gap-2">
            <FormLabel htmlFor="account-type-toggle">
              {microcopy.accountTypeLabel}
            </FormLabel>
            <Controller
              control={control}
              name="accountType"
              render={({ field }) => (
                <AccountTypeToggleGroup
                  userName={userName}
                  userProfilePicture={userProfilePicture}
                  publisherName={publisherName}
                  value={field.value || 'personal'}
                  microcopy={microcopy.accountType}
                  onValueChange={field.onChange}
                />
              )}
            />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <FormLabel htmlFor="visibility-toggle">
            {microcopy.visibilityLabel}
          </FormLabel>
          <Controller
            control={control}
            name="visibility"
            render={({ field }) => (
              <VisibilityToggleGroup
                value={field.value}
                microcopy={microcopy.visibility}
                onValueChange={field.onChange}
              />
            )}
          />
        </div>
      </form>
    </div>
  );

  return (
    <FormModal
      dark
      size="xsmall"
      open={open}
      onOpenChange={handleOpenChange}
      trigger={triggerElement}
      title={microcopy.title}
      subtitle={microcopy.subTitle}
      close={microcopy.close}
      submit={microcopy.cta}
      disabled={!isValid || isSubmitting || limitReached}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(onFormSubmit)}
      onCancel={handleOnCancelFavorite}
      content={formContent}
      position={position}
      showBackdrop={showBackdrop}
      className={c('h-auto max-h-[468px] md:max-w-[420px]', className)}
    />
  );
};

FavoriteListModal.displayName = 'FavoriteListModal';
